import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Section, StatCard } from '@/components/db/parts';
import { Device } from '@/lib/device';
import { fetchJsonServiceInfo } from '@/api/endpoints';
import type { JsonServiceInfo } from '@/api/types';

function fmtUptime(s: number | null): string {
  if (s == null) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function Json({ device }: { device: Device }) {
  const qc = useQueryClient();
  const { data: info, isLoading } = useQuery<JsonServiceInfo>({
    queryKey: ['json', 'info'],
    queryFn: fetchJsonServiceInfo,
    refetchInterval: 5000,
  });

  const down = info && !info.reachable;

  return (
    <PageShell device={device} title="Service de Réception JSON" breadcrumb="Services">
      <div className="p-4 md:p-6 space-y-4">

        {/* ── État (temps réel) ── */}
        <Section title="État (temps réel · 5 s)" action={
          info ? (
            <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${info.reachable && info.health.ok ? 'badge-green' : 'badge-slate'}`}>
              {info.reachable ? (info.health.ok ? 'up' : 'dégradé') : 'injoignable'}
            </span>
          ) : null
        }>
          {isLoading || !info ? (
            <div className="text-sm text-muted-foreground">Chargement…</div>
          ) : down ? (
            <div className="text-sm p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/40">
              ⚠ Service <strong>{info.configured ? 'injoignable' : 'non configuré'}</strong> —{' '}
              {info.configured
                ? 'reception_json ne répond pas (réessai automatique).'
                : <>la variable d&apos;environnement <code className="font-mono">NODE_WEEWX_JSON_URL</code> n&apos;est pas définie côté admin.</>}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Fichiers écrits" value={info.totals.filesWritten} sub="cumul depuis boot" tone="primary" />
                <StatCard label="Requêtes" value={info.totals.requests} sub="total /sendjson" />
                <StatCard label="Rejets concurrence" value={info.totals.concurrencyRejected} sub="HTTP 429" tone={info.totals.concurrencyRejected > 0 ? 'warn' : 'default'} />
                <StatCard label="Uptime" value={fmtUptime(info.process.uptimeS)} sub={info.process.rssMb != null ? `${info.process.rssMb} MB RSS` : undefined} />
              </div>

              {info.byStatus.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5">Requêtes par code HTTP</div>
                  <div className="flex flex-wrap gap-2">
                    {info.byStatus.map((s) => (
                      <span key={s.statut} className={`px-2 py-0.5 rounded text-xs font-mono ${s.statut.startsWith('2') ? 'badge-green' : s.statut.startsWith('4') ? 'badge-amber' : 'badge-slate'}`}>
                        {s.statut}: {s.nb}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {info.perStation.length > 0 && (
                <div className="overflow-x-auto">
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5">Fichiers écrits par station</div>
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">station</th>
                        <th className="text-right px-3 py-2">fichiers</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {info.perStation.map((p) => (
                        <tr key={p.meteor}>
                          <td className="px-3 py-2 font-mono text-xs">{p.meteor}</td>
                          <td className="px-3 py-2 font-mono text-xs text-right">{p.files}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ── Actions (none upstream) ── */}
        <Section title="Actions">
          <p className="text-sm text-muted-foreground">
            <code className="font-mono">reception_json</code> est un récepteur passif : les stations WeeWX poussent leur JSON sur{' '}
            <code className="font-mono">POST /sendjson</code> et le service écrit le fichier sur disque (ingéré ensuite par node_climato).
            Il n&apos;expose <strong>aucune action</strong> (pas de file, pas de worker). Le cycle de vie (start/stop/restart) passe par Clever Cloud.
          </p>
        </Section>

      </div>
    </PageShell>
  );
}
